/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import { PREMIUM_EDITION } from '@jira/platform__application-editions/src/edition.tsx';
import type { TenantContext } from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { ONBOARDING_SELECTED_PROJECT_TEMPLATE_KEY } from '@jira/platform__onboarding-core/src/constants.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import {
	KEY_OPEN,
	KEY_DISMISSED,
	KEY_OPEN_TASK,
	KEY_TASK_GROUP,
	KEY_HAS_EVER_SEEN_ROUTE,
	KEY_JSW_ONBOARDING,
	KEY_PREMIUM_TRIAL_REOPENED,
	QUICKSTART_TYPE,
	getBackendVisibilityKey,
	QUICKSTART_UI_STATUS,
	JSM_BUSINESS_TASKS,
	allTasksExcludingJSMBusinessTasks,
} from '../../../common/constants.tsx';
import { pollBackend } from '../../../common/utils/poll-backend.tsx';
import { fireAPIErrorAnalytics } from '../../../utils/analytics/index.tsx';
import { getCrossProjectBoardTrait } from '../../cross-project-board-to-plan-upsell-personalization-trait/index.tsx';
import type { Attribute } from '../../cross-project-board-to-plan-upsell-personalization-trait/types.tsx';
import type { State } from '../../model/types.tsx';
import { getOnboardingSkipApplicationProperty } from '../../skip-onboarding/index.tsx';
import { setLocalStorageItem } from '../../utils.tsx';
import { nextSetChecklistIsDismissed } from '../next-checklist-dismiss/index.tsx';
import type { Props } from '../types.tsx';

const allTasks = [...allTasksExcludingJSMBusinessTasks, ...JSM_BUSINESS_TASKS];

type RefreshAppStateProps = {
	canEditPlans: boolean;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	projectType?: ProjectType;
	containerType?: 'project' | 'plan';
} & Pick<TenantContext, 'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'>;

export const refreshAppState =
	({
		atlassianAccountId,
		cloudId,
		appEditions,
		isAdmin,
		canEditPlans,
		createAnalyticsEvent,
		projectType,
		containerType = 'project',
	}: RefreshAppStateProps): Action<State, Props> =>
	async ({ getState, setState, dispatch }) => {
		// Need to check to see if there is anything in the backend to take that as source of truth
		// If there is nothing then use defaults
		// Check backend for flags that are marked as completed (true)
		// Carry over state from previous actions
		// Create Completed Actions

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const updatedState: Record<string, any> = {};

		const { projectState = null } = getState();

		// Open QS Checklist as default state if state cannot be determined from the backend
		updatedState.isOpen = true;
		updatedState.isDismissed = false;
		updatedState.quickstartStateProjectType = projectType;
		updatedState.containerType = containerType;
		let backendVisibilityKeys: string[] = [];
		let jsmTaskProjectKeys: string[] = [];
		// Open QS Checklist as default state if state cannot be determined from the backend
		updatedState.visibility = Object.values(QUICKSTART_TYPE).reduce<
			Record<QUICKSTART_TYPE, string>
		>(
			(acc, type) => {
				acc[type] = QUICKSTART_UI_STATUS.OPEN;
				return acc;
			},
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			{} as Record<QUICKSTART_TYPE, string>,
		);

		backendVisibilityKeys = Object.values(QUICKSTART_TYPE).map((type) =>
			getBackendVisibilityKey(type),
		);
		if (projectState?.projectId)
			jsmTaskProjectKeys = JSM_BUSINESS_TASKS.map((task) => `${task}.${projectState?.projectId}`);

		const backendState = await pollBackend({
			keys: [
				KEY_OPEN,
				KEY_DISMISSED,
				KEY_OPEN_TASK,
				KEY_TASK_GROUP,
				KEY_HAS_EVER_SEEN_ROUTE,
				KEY_JSW_ONBOARDING,
				KEY_PREMIUM_TRIAL_REOPENED,
				...allTasks,
				...backendVisibilityKeys,
				...jsmTaskProjectKeys,
			],
		});

		const backendStateExists = backendState !== undefined && Object.keys(backendState).length > 0;

		if (backendStateExists) {
			updatedState.completedTaskList = Object.keys(backendState).filter(
				(key) =>
					[...allTasksExcludingJSMBusinessTasks, ...jsmTaskProjectKeys].includes(key) &&
					backendState[key] === true,
			);

			Object.values(QUICKSTART_TYPE).forEach((type: QUICKSTART_TYPE) => {
				// use new state if possible
				if (getBackendVisibilityKey(type) in backendState) {
					updatedState.visibility[type] = backendState[getBackendVisibilityKey(type)];
					// else fallback to old state
				} else if (KEY_DISMISSED in backendState && backendState[KEY_DISMISSED]) {
					updatedState.visibility[type] = QUICKSTART_UI_STATUS.DISMISSED;
				} else if (KEY_OPEN in backendState) {
					updatedState.visibility[type] = backendState[KEY_OPEN]
						? QUICKSTART_UI_STATUS.OPEN
						: QUICKSTART_UI_STATUS.MINIMIZED;
				}
				setLocalStorageItem(`${type}.visibility`, updatedState.visibility[type]);
			});

			// Delete when cleaning up isDismissed in state
			if (KEY_DISMISSED in backendState) {
				updatedState.isDismissed = backendState[KEY_DISMISSED];
			}
			// Delete when cleaning up isOpen in state
			if (KEY_OPEN in backendState) {
				updatedState.isOpen = backendState[KEY_OPEN];
			}
			if (KEY_OPEN_TASK in backendState) {
				updatedState.openTask = backendState[KEY_OPEN_TASK];
			}
			if (KEY_HAS_EVER_SEEN_ROUTE in backendState) {
				updatedState.hasEverSeenRoute = backendState[KEY_HAS_EVER_SEEN_ROUTE];
			}

			if (KEY_TASK_GROUP in backendState) {
				updatedState.currentTaskGroupParent =
					// @ts-expect-error - TS2571 - Object is of type 'unknown'.
					backendState[KEY_TASK_GROUP].taskGroup.currentTaskGroupParent;
				updatedState.previousTaskGroupParents =
					// @ts-expect-error - TS2571 - Object is of type 'unknown'.
					backendState[KEY_TASK_GROUP].taskGroup.previousTaskGroupParents;
			}

			if (KEY_JSW_ONBOARDING in backendState) {
				if (
					typeof backendState[KEY_JSW_ONBOARDING] === 'object' &&
					backendState[KEY_JSW_ONBOARDING] !== null &&
					ONBOARDING_SELECTED_PROJECT_TEMPLATE_KEY in backendState[KEY_JSW_ONBOARDING]
				) {
					updatedState.selectedProjectTemplateKeyFromOnboarding =
						backendState[KEY_JSW_ONBOARDING][ONBOARDING_SELECTED_PROJECT_TEMPLATE_KEY];
				}
			}
		}
		setState(updatedState);

		if (!(backendStateExists && KEY_OPEN in backendState)) {
			try {
				const skipApplicationProperty = await getOnboardingSkipApplicationProperty();
				if (skipApplicationProperty.quickstart) {
					dispatch(nextSetChecklistIsDismissed(true));
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireAPIErrorAnalytics(error, 'skipOnboarding');
			}
		}

		const setPremiumCardAudienceTrait = async (accountId: string | null) => {
			if (accountId === null) {
				return;
			}

			const trait: Attribute | undefined = await getCrossProjectBoardTrait(
				createAnalyticsEvent,
				cloudId,
				accountId,
			);

			setState({
				isCrossProjectBoardToPlanUpsell: trait?.value === true || false,
			});
		};
		// We only want to check if a non-admin user has the CPB trait if they are in a
		// premium evaluation of jira software (i.e. they have access to Advanced Roadmaps)
		if (appEditions.software === PREMIUM_EDITION && !isAdmin) {
			await setPremiumCardAudienceTrait(atlassianAccountId);
		}

		setState({ canEditPlans });
		setState({ isQuickstartLoaded: true });
	};
